import type { StatusTone } from '@beonit/adaptive-raindrop';
import type { AnalysisRun, RunStatus } from '../model.js';
import { degradationOf, isLegacyRun, triggerLabel } from './run-labels.js';
import { DEFAULT_HEARTBEAT_TTL_S, formatDuration, isHeartbeatStale } from './queue-presentation.js';

/**
 * What survives turning a queue card into a table row.
 *
 * The lanes were cards 154-220 px tall, so twenty runs waiting were about three screens of
 * scrolling and the run history deliberately placed «one question away» underneath was three
 * screens away instead. Rows of 32-40 px fix that, and this module holds what compaction costs and
 * what is given back — because the `.vue` files of this repo are checked by eye in a browser, and
 * «the row still offers the action that matters» is not something an eye verifies on a lane that
 * is empty in local.
 *
 * ## One table, one set of columns
 *
 * There were three lanes — running, waiting, just finished — each with its own columns, and a
 * history table under them with others. Four tables to answer «what is going on with the analyses»
 * made the same run appear in two of them and the question of where to look come before the
 * answer. Now there is one table of every run, newest launch first, and one set of columns that
 * means the same thing on every row. What used to set the lanes apart lives in the cells: the
 * progress cell is a bar for a run a worker has started and the place in the queue for one that is
 * still waiting, and the actions cell offers what that run's state allows.
 */

export interface RunColumn {
  key: string;
  /** Empty for a column whose header would only repeat what the cells obviously are. */
  label: string;
  /** What a screen reader hears for a column with no visible label. */
  srLabel?: string;
  /** Width and alignment. Kept beside the column so the table cannot drift from its header. */
  class?: string;
  numeric?: boolean;
}

const RUN_COLUMNS: readonly RunColumn[] = Object.freeze([
  { key: 'source', label: 'Origen' },
  { key: 'state', label: 'Estado', class: 'whitespace-nowrap' },
  { key: 'progress', label: 'Progreso', class: 'w-32' },
  { key: 'launched', label: 'Lanzada', class: 'w-24', numeric: true },
  { key: 'elapsed', label: 'Duración', class: 'w-20', numeric: true },
  { key: 'engines', label: 'Motores' },
  { key: 'config', label: 'Configuración' },
  { key: 'flags', label: '', srLabel: 'Avisos', class: 'w-9' },
  // As wide as its buttons: a queued run offers two arrows and «quitar», a finished one three.
  { key: 'actions', label: '', srLabel: 'Acciones', class: 'w-px whitespace-nowrap' },
]);

/**
 * The checkbox column, only while the table is selecting.
 *
 * First, where the eye starts a row, and absent otherwise: a column of checkboxes on a screen read
 * all day is width taken from the source name for an action taken now and then.
 */
const SELECT_COLUMN: RunColumn = Object.freeze({
  key: 'select',
  label: '',
  srLabel: 'Selección',
  class: 'w-9',
});

export function runColumns(options: { selectable?: boolean } = {}): RunColumn[] {
  return options.selectable ? [SELECT_COLUMN, ...RUN_COLUMNS] : [...RUN_COLUMNS];
}

// ── Filtering by state ───────────────────────────────────────────────────────

export type RunStatusFilter =
  'waiting' | 'paused' | 'running' | 'succeeded' | 'failed' | 'cancelled';

/**
 * The states as the operator asks about them, which is not how the ledger spells them.
 *
 * Eight statuses, six questions. A dispatched run is still waiting for a worker to pick it up, so
 * it is «en cola»; a run asked to stop keeps running until its next checkpoint, so it is «en
 * ejecución» — «cancelada» would be a lie while it is still consuming a slot. A paused one is its
 * own question: it waits for somebody to resume its execution, not for a slot.
 */
export const RUN_STATUS_FILTERS: ReadonlyArray<{
  key: RunStatusFilter;
  label: string;
  statuses: readonly RunStatus[];
}> = Object.freeze([
  { key: 'waiting', label: 'En cola', statuses: ['QUEUED', 'DISPATCHED'] },
  { key: 'paused', label: 'En pausa', statuses: ['PAUSED'] },
  { key: 'running', label: 'En ejecución', statuses: ['RUNNING', 'CANCEL_REQUESTED'] },
  { key: 'succeeded', label: 'Completadas', statuses: ['SUCCEEDED'] },
  { key: 'failed', label: 'Fallidas', statuses: ['FAILED'] },
  { key: 'cancelled', label: 'Canceladas', statuses: ['CANCELLED'] },
]);

/** What to ask the server for. Nothing chosen asks for every run, not for none. */
export function statusesFor(chosen: readonly RunStatusFilter[]): RunStatus[] | undefined {
  if (chosen.length === 0) return undefined;

  return RUN_STATUS_FILTERS.filter((filter) => chosen.includes(filter.key)).flatMap((filter) => [
    ...filter.statuses,
  ]);
}

/**
 * The left border of a row, which replaces the visual weight a whole card used to carry.
 *
 * A border and not a row background: a tinted background fights the hover state and the striping,
 * and at 32 px a full-width wash turns a table into a heat map nobody can read.
 */
export function rowTone(run: AnalysisRun, now: number): StatusTone | null {
  if (run.status === 'FAILED' || run.error) return 'danger';

  // A worker nobody has heard from. This is the row that most needs attention, and the one a
  // compact lane would otherwise make indistinguishable from the rest.
  if (isHeartbeatStale(run, now, DEFAULT_HEARTBEAT_TTL_S)) return 'warning';

  if (degradationOf(run.engine, run.engine?.degraded === true)) return 'warning';
  if (run.cancellation?.ignoredBecauseFinishing) return 'warning';

  return null;
}

export interface ProgressReading {
  show: boolean;
  value: number;
  step: string | null;
}

/**
 * Whether a bar means anything yet.
 *
 * Keyed on `startedAt` and not on the status, which is the rule the card already had and the
 * reason is worth keeping: a dispatched run is in motion but no worker has picked it up, so its
 * progress is 0 and stays there. A bar sitting at 0 % reads as a run that is stuck rather than one
 * that has not begun.
 */
export function progressReading(run: AnalysisRun): ProgressReading {
  return {
    show: Boolean(run.startedAt) && !run.finishedAt,
    value: run.progress,
    step: run.currentStep,
  };
}

export type ProgressCell =
  | { kind: 'bar'; value: number; step: string | null }
  | { kind: 'position'; position: number }
  | { kind: 'none' };

/**
 * What the progress cell shows, which depends on where the run is.
 *
 * A bar once a worker has started it — `progressReading` decides that, and why a dispatched run
 * gets no bar. Its place in the queue while it waits, which is the order the arrows beside it
 * change; the ledger does not carry it, so it comes from the queue. Nothing otherwise: a finished
 * run is described by its state and its duration, and a dispatched one has left the order without
 * starting.
 */
export function progressCell(run: AnalysisRun, queueIndex: number | null): ProgressCell {
  const reading = progressReading(run);

  if (reading.show) return { kind: 'bar', value: reading.value, step: reading.step };
  if (run.status === 'QUEUED' && queueIndex !== null)
    return { kind: 'position', position: queueIndex };

  return { kind: 'none' };
}

export interface RowFlag {
  key: string;
  label: string;
  tone: StatusTone | null;
  title: string | null;
  /**
   * Whether it stays on the row instead of moving into the expanded detail.
   *
   * Only for the things somebody would act wrongly without.
   */
  alwaysVisible: boolean;
}

/**
 * The badges a compact row keeps, and which of them may not be hidden.
 *
 * Whether a run replaces the video's transcription is not one of them: in the back-office a run
 * that ends well is the latest complete one either way, so the badge only added noise.
 */
export function rowFlags(run: AnalysisRun, now: number): RowFlag[] {
  const flags: RowFlag[] = [];

  // On the row, not in the detail: nobody in the team launched it, and a run nobody remembers
  // asking for is the first thing somebody reading the queue wants explained.
  if (run.trigger === 'auto_retry') {
    flags.push({
      key: 'auto_retry',
      label: triggerLabel(run.trigger),
      tone: null,
      title:
        'Virtual Profiler la relanzó solo porque la anterior falló. Si esta también falla, no lo vuelve a intentar.',
      alwaysVisible: true,
    });
  }

  if (run.attempt > 1) {
    flags.push({
      key: 'attempt',
      label: `Intento ${run.attempt}`,
      tone: null,
      title: null,
      alwaysVisible: false,
    });
  }

  const degraded = degradationOf(run.engine, run.engine?.degraded === true);

  if (degraded) {
    flags.push({
      key: 'degraded',
      label: 'Degradado',
      tone: 'warning',
      title: degraded.join(' · ') || null,
      alwaysVisible: false,
    });
  }

  for (const skipped of run.enginesSkipped) {
    flags.push({
      key: `skipped:${skipped.name}`,
      label: `${skipped.name} omitido`,
      tone: 'warning',
      title: skipped.reason,
      alwaysVisible: false,
    });
  }

  if (isHeartbeatStale(run, now, DEFAULT_HEARTBEAT_TTL_S)) {
    flags.push({
      key: 'heartbeat_stale',
      label: 'Sin señales del worker',
      tone: 'warning',
      title: 'Hace demasiado que no da señales de vida.',
      // Not on the row itself: `rowTone` already paints the left border amber for it and the
      // signals column carries the icon, so a third copy would only cost the width the source
      // name needs. Verified in the browser, where a dataset of stale runs squeezed every row.
      alwaysVisible: false,
    });
  }

  // Cancelled too late: the worker was already writing its result and finished anyway. Without
  // this, «cancelada» sends somebody looking for a result that does exist.
  if (run.cancellation?.ignoredBecauseFinishing) {
    flags.push({
      key: 'cancelled_too_late',
      label: 'Terminó pese a la cancelación',
      tone: null,
      title: 'Se pidió cancelar cuando ya estaba guardando el resultado.',
      alwaysVisible: false,
    });
  }

  // Backfilled from before the ledger: no versions, no configuration, no stages.
  if (isLegacyRun(run)) {
    flags.push({
      key: 'legacy',
      label: 'Previo al versionado',
      tone: null,
      title: 'Reconstruido del histórico: sin versiones ni configuración.',
      alwaysVisible: false,
    });
  }

  return flags;
}

/**
 * How long it has been going, or how long it took.
 *
 * Running from the start; finished from start to finish, so it stops moving. A run that never
 * started says nothing rather than «0 s», which would read as instant.
 */
export function elapsedFor(run: AnalysisRun, now: number): string {
  if (!run.startedAt) return '';

  return formatDuration(run.startedAt, run.finishedAt ?? now);
}
